import { useCallback, useId, useState } from 'react';
import { Field, Form, Formik } from 'formik';
import { DialogContent } from '@mui/material';
import { getApiErrorMessage } from '@api/getApiError';
import FormikModal from '@components/Modals/FormikModal';
import FormikModalHeader from '@components/Modals/FormikModalHeader';
import { useModalProvider } from '@components/Modals/ModalContext';
import { VesselFormValidationSchema, VesselForm, VesselFormikModalProps } from './types';
import { useAddVesselMutation } from '@api/Trinium/Endpoints/Vessels/slice';
import { FormikTextInput } from '../Forms/Components/TextInput';

import styles from './VesselModal.module.css';

/**
 * Formik Modal for a Vessel
 */
export default function VesselFormikModal(props: VesselFormikModalProps) {
	const { queueModalAnnouncement } = useModalProvider();
	const [isOpen, setIsOpen] = useState(true);
	const formId = useId();
	const [addVessel] = useAddVesselMutation({
		fixedCacheKey: props.apiFixedCacheKey
	});
	const initialValues: VesselForm = {
		code: '',
		name: ''
	};
	const closeModal = useCallback(() => {
		setIsOpen(false);
	}, []);

	const handleSubmit = useCallback(
		async (values: VesselForm) => {
			if (values.name.length > 0 && values.code.length > 0) {
				await addVessel({
					id: values.code,
					name: values.name
				})
					.unwrap()
					.then(() => {
						closeModal();
					})
					.catch((error: unknown) => {
						const errorMessage = getApiErrorMessage(error);
						queueModalAnnouncement({
							severity: 'error',
							message: errorMessage
						});
					});
			}
		},
		[addVessel, queueModalAnnouncement, closeModal]
	);

	return (
		<FormikModal
			isOpen={isOpen}
			onClose={closeModal}
			paperClass={styles['modal']}
		>
			<Formik
				initialValues={initialValues}
				validationSchema={VesselFormValidationSchema}
				onSubmit={handleSubmit}
			>
				<>
					<FormikModalHeader
						title='Vessel'
						formName={formId}
						onClose={closeModal}
					/>

					<DialogContent>
						<Form
							id={formId}
							className={styles['form']}
						>
							<Field
								name='code'
								component={FormikTextInput}
								label='Vessel Code:'
								labelPlacement='start'
								className={styles['vessel-input']}
								required
							/>

							<Field
								name='name'
								component={FormikTextInput}
								label='Vessel Name:'
								labelPlacement='start'
								className={styles['vessel-input']}
								required
							/>
						</Form>
					</DialogContent>
				</>
			</Formik>
		</FormikModal>
	);
}
